import React, { ReactNode, useCallback } from 'react';
import { useAnalyticsEvents, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import styled from '@emotion/styled';
import Button from '@atlaskit/button';
import { N700 } from '@atlaskit/theme/colors';

export type ProductSelectButtonProps = {
  readonly productIcon: ReactNode;
  readonly productName: ReactNode;
  readonly productDescription: ReactNode;
  readonly buttonTitle: ReactNode;
  readonly className: string;
  readonly analyticsId: string;
  readonly analyticsLabel: string | undefined;
  readonly onClick: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    analyticsEvent: UIAnalyticsEvent | null
  ) => void;
  readonly testId?: string;
};

export function ProductSelectButton({
  productIcon,
  productName,
  productDescription,
  buttonTitle,
  className,
  analyticsId,
  analyticsLabel,
  onClick = () => undefined,
  testId
}: ProductSelectButtonProps) {
  const { createAnalyticsEvent } = useAnalyticsEvents();

  const clickHandler = useCallback(
    (clickEvent) => {
      const analyticsEvent = createAnalyticsEvent({
        event: 'clicked',
        eventComponent: 'button',
        eventName: 'signUpProductSelectButtonClicked',
        uuid: analyticsId,
        label: analyticsLabel
      });

      // do what you like with the event, fire / modify / clone
      // this here is the typical usage pattern for atlaskit components

      const clonedEvent = analyticsEvent.clone();

      // channel name gets passed to fire(), but we aren't using channels, so it's left blank
      analyticsEvent.fire();

      // firing is prevented from happening more than once
      // so that's why we pass a clone to be accessed by the parent component
      // which might also want to fire their own event on this ui interaction
      onClick(clickEvent, clonedEvent);
    },
    [createAnalyticsEvent, onClick]
  );

  return (
    <Container className={className}>
      <LogoContent>
        {productIcon}
        <LogoTextContainer>
          <ProductName>{productName}</ProductName>
          <ProductDescription>{productDescription}</ProductDescription>
        </LogoTextContainer>
      </LogoContent>
      <StyledButton appearance="primary" onClick={clickHandler} testId={testId}>
        {buttonTitle}
      </StyledButton>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  margin-bottom: 32px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const LogoContent = styled.div`
  display: flex;
  flex-direction: row;
`;

const LogoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  margin-left: 11px;
`;

const ProductName = styled.div`
  font-family: Charlie Display;
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 16px;
  color: ${N700};
`;

const ProductDescription = styled.div`
  font-family: Charlie Text;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 18px;
  color: ${N700};
`;

const StyledButton = styled(Button)`
  font-family: Charlie Text;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: center;
`;
